import { Container, Graphics } from "pixi.js";
import type { PartDefinition, PartVariant } from "@/lib/game/types";

type ParticleShape = "circle" | "star" | "diamond" | "ring";

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: number;
  alpha: number;
  shape: ParticleShape;
  spin: number;
}

const RAINBOW_COLORS = [0xff3b30, 0xff9500, 0xffcc00, 0x34c759, 0x007aff, 0x5856d6, 0xaf52de];

interface VariantFxConfig {
  colors: number[];
  rate: number;
  speed: number;
  spread: number;
  maxParticles: number;
  minSize: number;
  maxSize: number;
  shapes: ParticleShape[];
}

export function partHasParticleFx(part: PartDefinition): boolean {
  const variant = part.style.variant ?? "standard";
  return (
    variant === "trail" ||
    variant === "nova" ||
    variant === "galaxy" ||
    variant === "shiny" ||
    variant === "golden"
  );
}

function variantParticleConfig(variant: PartVariant): VariantFxConfig {
  switch (variant) {
    case "trail":
      return {
        colors: RAINBOW_COLORS,
        rate: 36,
        speed: 72,
        spread: 52,
        maxParticles: 72,
        minSize: 5,
        maxSize: 16,
        shapes: ["star", "circle", "diamond", "ring"],
      };
    case "nova":
      return {
        colors: [0xff9ff3, 0xf368e0, 0xffffff, 0xffd6fa],
        rate: 28,
        speed: 58,
        spread: 44,
        maxParticles: 56,
        minSize: 4,
        maxSize: 14,
        shapes: ["star", "circle"],
      };
    case "galaxy":
      return {
        colors: [0x48dbfb, 0x686de0, 0xffffff, 0x9b59b6],
        rate: 22,
        speed: 34,
        spread: 48,
        maxParticles: 48,
        minSize: 3,
        maxSize: 12,
        shapes: ["star", "circle", "ring"],
      };
    case "shiny":
      return {
        colors: [0xffffff, 0xecf0f1, 0xbdc3c7, 0xfff9e6],
        rate: 18,
        speed: 28,
        spread: 30,
        maxParticles: 40,
        minSize: 3,
        maxSize: 10,
        shapes: ["star", "diamond"],
      };
    case "golden":
      return {
        colors: [0xfff3b0, 0xf4d03f, 0xffffff, 0xffe082],
        rate: 20,
        speed: 38,
        spread: 34,
        maxParticles: 44,
        minSize: 4,
        maxSize: 12,
        shapes: ["star", "circle"],
      };
    default:
      return {
        colors: [0xffffff],
        rate: 0,
        speed: 0,
        spread: 0,
        maxParticles: 0,
        minSize: 0,
        maxSize: 0,
        shapes: ["circle"],
      };
  }
}

export class PartParticleFx {
  readonly container = new Container();
  private gfx = new Graphics();
  private sparks: Spark[] = [];
  private spawnAccum = 0;
  private readonly config: VariantFxConfig;
  private colorIndex = 0;

  constructor(part: PartDefinition) {
    this.config = variantParticleConfig(part.style.variant ?? "standard");
    this.gfx.blendMode = "add";
    this.container.addChild(this.gfx);
    this.container.eventMode = "none";
  }

  tick(dt: number) {
    if (this.config.rate <= 0) return;

    this.spawnAccum += dt;
    const spawnEvery = 1 / this.config.rate;
    while (this.spawnAccum >= spawnEvery && this.sparks.length < this.config.maxParticles) {
      this.spawnAccum -= spawnEvery;
      this.spawnSpark();
    }

    for (const s of this.sparks) {
      s.life += dt;
      s.vy += dt * 14;
      s.vx *= 0.985;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.size += dt * (s.shape === "ring" ? 10 : 14);
      s.spin += dt * 4;
    }

    this.sparks = this.sparks.filter((s) => s.life < s.maxLife);
    this.draw();
  }

  private spawnSpark() {
    const color =
      this.config.colors[this.colorIndex % this.config.colors.length] ?? 0xffffff;
    this.colorIndex += 1;
    const shape = this.config.shapes[Math.floor(Math.random() * this.config.shapes.length)]!;
    const angle = Math.PI / 2 + (Math.random() - 0.5) * 1.35;
    const speed = this.config.speed * (0.6 + Math.random() * 0.8);
    const size =
      this.config.minSize + Math.random() * (this.config.maxSize - this.config.minSize);

    this.sparks.push({
      x: (Math.random() - 0.5) * this.config.spread * 0.55,
      y: (Math.random() - 0.5) * 12 + this.config.spread * 0.08,
      vx: Math.cos(angle) * speed * 0.22 + (Math.random() - 0.5) * 18,
      vy: Math.sin(angle) * speed,
      life: 0,
      maxLife: 0.45 + Math.random() * 0.75,
      size,
      color,
      alpha: 0.98,
      shape,
      spin: Math.random() * Math.PI * 2,
    });
  }

  private drawShape(s: Spark, alpha: number) {
    const r = s.size * (1 - (s.life / s.maxLife) * 0.25);
    if (s.shape === "star") {
      this.gfx.star(s.x, s.y, 5, r, r * 0.45, s.spin).fill({ color: s.color, alpha });
    } else if (s.shape === "diamond") {
      this.gfx
        .moveTo(s.x, s.y - r)
        .lineTo(s.x + r * 0.65, s.y)
        .lineTo(s.x, s.y + r)
        .lineTo(s.x - r * 0.65, s.y)
        .closePath()
        .fill({ color: s.color, alpha });
    } else if (s.shape === "ring") {
      this.gfx.circle(s.x, s.y, r).stroke({ width: Math.max(2, r * 0.22), color: s.color, alpha });
      this.gfx.circle(s.x, s.y, r * 0.45).fill({ color: s.color, alpha: alpha * 0.45 });
    } else {
      this.gfx.circle(s.x, s.y, r).fill({ color: s.color, alpha });
      if (r > 4) {
        this.gfx.circle(s.x - r * 0.2, s.y - r * 0.2, r * 0.28).fill({
          color: 0xffffff,
          alpha: alpha * 0.45,
        });
      }
    }
  }

  private draw() {
    this.gfx.clear();
    for (const s of this.sparks) {
      const t = s.life / s.maxLife;
      const alpha = s.alpha * (1 - t * t);
      if (alpha <= 0.02) continue;
      this.drawShape(s, alpha);
    }
  }
}

export function attachPartParticleFx(wrap: Container, part: PartDefinition): PartParticleFx | null {
  if (!partHasParticleFx(part)) return null;

  const fx = new PartParticleFx(part);
  wrap.addChildAt(fx.container, 0);

  let last = performance.now();
  wrap.onRender = () => {
    const now = performance.now();
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    fx.tick(dt);
  };

  return fx;
}

export function partArtGraphics(wrap: Container): Container | null {
  for (const child of wrap.children) {
    if (child instanceof Container && child.label === "part-art") {
      return child;
    }
  }
  return null;
}
